import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getInitials } from './utils.js';
import { LoginView } from './components/LoginView.jsx';
import { ResetPasswordView } from './components/ResetPasswordView.jsx';
import { StatCard } from './components/StatCard.jsx';
import { KanbanColumn } from './components/KanbanColumn.jsx';
import { LeadDetailsModal, resolveInitialTab } from './components/LeadDetailsModal.jsx';
import { CreateBloggerModal } from './components/CreateBloggerModal.jsx';
import QuickAddStrip from './components/QuickAddStrip.jsx';
import MobileAddPage from './components/MobileAddPage.jsx';
import CsvImportModal from './components/CsvImportModal.jsx';
import { FilterDropdown } from './components/FilterDropdown.jsx';
import { managerFilterUsers } from './lib/managerFilterUsers.js';
import { ManagerCreateModal } from './components/ManagerCreateModal.jsx';
import { Logo } from './components/Logo.jsx';
import { Sidebar } from './components/Sidebar.jsx';
import { AccountantRouteGuard } from './components/AccountantRouteGuard.jsx';
import { OutreachProvider } from './contexts/OutreachContext';
import { LoadingSpinner } from './components/LoadingSpinner';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';

const BloggersView = lazy(() => import('./components/BloggersView'));
const OutreachLayout = lazy(() => import('./components/outreach/OutreachLayout').then(m => ({ default: m.OutreachLayout })));
const MyStatisticsPage = lazy(() => import('./components/MyStatisticsPage'));
const TeamPage = lazy(() => import('./components/TeamPage'));
const SettingsPanel = lazy(() => import('./components/SettingsPanel').then(m => ({ default: m.SettingsPanel })));
// POST-W1 T3 — ReplyioMappingPage is no longer routed here; it mounts inside SettingsPanel.
const AmbassadorsView = lazy(() => import('./components/AmbassadorsView'));
const InvoicesView = lazy(() => import('./components/InvoicesView'));
const InboxPage = lazy(() => import('./components/InboxPage'));
// HIDDEN 2026-09-24 (owner, M2-HIDE-OLD): /monitoring redirects to /monitoring2; the page and its route wiring
// (see 6e3f2a1) stay restorable — MonitoringView.jsx and its components are kept.
// const MonitoringView = lazy(() => import('./components/MonitoringView')); // R7 phase 6 — rounds monitoring page
const Monitoring2View = lazy(() => import('./components/Monitoring2View')); // MONITORING2 §3.4 — «Мониторинг 2»
const MonitoringReportArchive = lazy(() => import('./components/MonitoringReportArchive.jsx'));
const MonitoringGeoView = lazy(() => import('./components/MonitoringGeoView.jsx'));
const AmbassadorPayoutView = lazy(() => import('./components/AmbassadorPayoutView'));
const AmbstatView = lazy(() => import('./components/AmbstatView')); // SCOPE-HOURLY-METRICS H2b — ambstat tab
const FinderView = lazy(() => import('./components/FinderView')); // BLOGGER-FINDER — «Поиск блогеров»
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { queryKeys } from './lib/queryKeys';
import { updateLead, fetchLeadsByStatus } from './api/leads.js';
import { buildLeadMetadata } from './utils.js';
import { fetchPendingReminders } from './api/reminders.js';
import { fetchUnreadByLead } from './api/notifications.js';
import { useAuth } from './hooks/useAuth';
import { useAdminData } from './hooks/useAdminData';
import { useDragDrop } from './hooks/useDragDrop';
import { useLeadEditor } from './hooks/useLeadEditor';
import { useStatuses } from './hooks/useStatuses';
import { useBoardStats } from './hooks/useBoardStats';
import { useLeadDetail } from './hooks/useLeadDetail';
import { useChangeLeadStatus } from './hooks/useChangeLeadStatus';
import { useDeleteLead as useDeleteLeadMutation } from './hooks/useDeleteLead';
import { useRealtimeInvalidation } from './hooks/useRealtimeInvalidation';
import { useToast } from './contexts/ToastContext';
import { NotificationBell } from './components/NotificationBell.jsx';
import { monitoringParams as monitoring2ListParams, monitoringReturnPath } from './lib/monitoringReportsState.mjs';
// Phase 97.8 W2: SyncStatusBadge import removed — component deleted with the Instantly cutover.

function App() {
  // ── Local UI state ──
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLeadInitialTab, setSelectedLeadInitialTab] = useState('info');
  const [incomingActivity, setIncomingActivity] = useState(null);
  const [activeTimers, setActiveTimers] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [busyLeadId, setBusyLeadId] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [lastContactFilter, setLastContactFilter] = useState('all');
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // ── Router ──
  const location = useLocation();
  const MonitoringComponent = location.pathname.startsWith('/monitoring2/geo') ? MonitoringGeoView : location.pathname.startsWith('/monitoring2/reports') ? MonitoringReportArchive : Monitoring2View;
  const navigate = useNavigate();
  const activeView = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/bloggers')) return 'bloggers';
    if (path.startsWith('/outreach')) return 'outreach';
    if (path.startsWith('/stats')) return 'stats';
    if (path.startsWith('/documents')) return 'documents';
    if (path.startsWith('/invoices')) return 'invoices';
    if (path.startsWith('/settings')) return 'settings';
    return 'kanban';
  }, [location.pathname]);

  // ── TanStack Query ──
  const queryClient = useQueryClient();
  const { data: statuses = [] } = useStatuses();
  const { data: boardStats } = useBoardStats();

  // ── Auth (after state so callbacks can reference setters) ──
  const handleAuthLogout = useCallback(() => {
    setSelectedLeadId(null);
    setManagerFilter('all');
    // admin state auto-resets via useAdminData when token becomes null
  }, []);
  const handleUnauthorized = useCallback(() => {
    setSelectedLeadId(null);
  }, []);
  const {
    token, user, isAdmin, isTeamLead, canManageTeam,
    impersonatedBy, authLoading,
    handleLogin, handleLogout, handleExitImpersonation, handleImpersonate
  } = useAuth({ queryClient, onLogout: handleAuthLogout, onUnauthorized: handleUnauthorized });

  // ── Admin/settings ──
  const {
    adminUsers, adminTeams, settingsLoading,
    managerForm,
    isManagerModalOpen, editingManager,
    openManagerModal, closeManagerModal, handleEditManager,
    handleManagerFormFieldChange,
    handleCreateManager, handleToggleManagerActive,
    handleCreateTeam, handleEditTeam, handleDeleteTeam,
  } = useAdminData({
    token, isAdmin, isTeamLead, activeView,
    queryClient, navigate,
    selectedLeadId, setSelectedLeadId, setIsDetailsOpen
  });

  // ── TanStack Query mutations ──
  const changeStatusMutation = useChangeLeadStatus();
  const deleteLeadMutation = useDeleteLeadMutation();
  const { addToast } = useToast();

  // ── Unread lead IDs (notification-based, единый «!») ──
  const { data: unreadByLead } = useQuery({
    queryKey: queryKeys.notifications.unreadByLead(),
    queryFn: fetchUnreadByLead,
    enabled: !!token,
    staleTime: 30_000,
  });
  const unreadCounts = unreadByLead?.counts ?? {};
  const unreadLeadIds = Object.keys(unreadCounts);

  // ── Pending reminders: server-side scheduler handles triggering every 60s.
  // On load we still check once to catch any that fired while CRM was closed.
  useEffect(() => {
    if (!token) return;
    fetchPendingReminders().catch(() => {});
  }, [token]);

  // Request browser notification permission on first login
  useEffect(() => {
    if (token && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [token]);

  const openLeadFromOutreach = useCallback((leadId) => {
    if (!leadId) return;
    setSelectedLeadId(leadId);
    navigate(`/?lead=${leadId}`);
    setIsDetailsOpen(true);
  }, [navigate]);

  const [deletingLeadId, setDeletingLeadId] = useState(null);

  const selectedLeadIdRef = useRef(selectedLeadId);
  const tokenRef = useRef(token);
  // activeViewRef, isAdminRef removed — SSE handled by useRealtimeInvalidation

  useEffect(() => {
    selectedLeadIdRef.current = selectedLeadId;
  }, [selectedLeadId]);

  // Sync URL ?lead=xxx or ?email=xxx with state (works on all routes)
  // Phase 57-02: extended to /documents + /invoices (R7 phase 8: /ambassadors + /testing retired)
  // so the registries' row click can open LeadDetailsModal from those pages
  // (previously only /, /bloggers triggered the modal).
  //
  // BLOGGER-CARD-STRUCT §6.7 (2026-08-19), P5 — two changes here:
  //   * the `tab` param is parsed AND assigned, so `?lead=<id>&card=1&tab=docs` lands the
  //     card on Документы (that URL is what both registries now navigate to);
  //   * Phase 58's panel-mode branch is GONE. It read «on /documents, ?lead alone =
  //     panel-mode — don't touch modal state» and existed only to stop LeadSidePanel and
  //     the modal double-opening. With the panel deleted the branch has no subject, and
  //     keeping it would make a bare ?lead= on /documents a DEAD URL — which is exactly
  //     the URL LeadJournalPopup.jsx:31-33 emits for form_submitted / contract_signed.
  //     Named F12 exception: the deep-link SEMANTICS of /documents?lead= deliberately
  //     change — bare ?lead= now opens the card on Инфо, like every other supported path.
  //     `?card=1` still parses and still works; it is simply no longer a gate.
  useEffect(() => {
    // SCOPE-AMBSTAT-VIEW E1 (AV-E-5, gap report G12): `/ambstat` joins the list so a row click
    // there opens the card OVER the tab instead of ejecting to the board and losing the
    // sub-tab, the filters and the scroll position. Same mechanism as `/monitoring`.
    const supportedPaths = ['/', '/bloggers', '/documents', '/invoices', '/inbox', '/monitoring2', '/ambstat'];
    const isSupportedPath = supportedPaths.includes(location.pathname)
        || location.pathname.startsWith('/outreach/') || location.pathname.startsWith('/ambstat/') || location.pathname.startsWith('/monitoring2/');
    if (!isSupportedPath) return;

    const params = new URLSearchParams(location.search);
    const leadParam = params.get('lead');
    const emailParam = params.get('email');

    // BCS §6.7 change 1 — parsed *and assigned*, on every supported-path run. The
    // assignment is UNCONDITIONAL: an absent `tab` assigns 'info', because a conditional
    // one would leave a previous visit's 'docs' in state and re-open the next tab-less
    // ?lead=<id> on Документы (AC-32's sub-check). Normalisation is the ONE normaliser of
    // §6.1 — the modal's exported resolveInitialTab — so an unknown or absent value falls
    // back to Инфо exactly as a bad `initialTab` prop does. Re-setting the same string is
    // a React no-op and this state is not in the dependency array, so it cannot loop.
    // BCS P6 — `.panelTab` alone LOSES the Instagram alias: resolveInitialTab('instagram')
    // is { panelTab: 'email', msgChannel: 'ig' }, so projecting the panel half only would
    // hand the modal 'email' and open Сообщения on the Email channel (AC-22). Round-trip
    // through the SAME single normaliser instead — no second vocabulary is introduced.
    const resolvedTab = resolveInitialTab(params.get('tab'));
    setSelectedLeadInitialTab(resolvedTab.msgChannel === 'ig' ? 'instagram' : resolvedTab.panelTab);

    if (!leadParam && !emailParam) {
      if (selectedLeadId) {
        setSelectedLeadId(null);
        setIsDetailsOpen(false);
      }
      return;
    }

    if (!user) return; // wait for auth

    if (leadParam) {
      // Re-open when leadParam changed OR when modal is closed but URL still
      // says a lead is selected (e.g. panel→modal swap on /documents).
      if (selectedLeadId !== leadParam || !isDetailsOpen) {
        setSelectedLeadId(leadParam);
        setIsDetailsOpen(true);
      }
    } else if (emailParam) {
      // Resolve email → leadId via API
      fetchLeadsByStatus({ search: emailParam, limit: 1 })
        .then(({ leads }) => {
          const match = leads.find(l => l.email?.toLowerCase() === emailParam.toLowerCase());
          if (match) {
            navigate(`${location.pathname}?lead=${match.id}`, { replace: true });
          }
        })
        .catch(() => {});
    }
  }, [location.search, location.pathname, selectedLeadId, isDetailsOpen, navigate, user]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Ref syncs for activeView/isAdmin removed — SSE handled by useRealtimeInvalidation


  const handleTimerExpire = useCallback((leadId) => {
    if (!leadId) return;
    setActiveTimers(prev => {
      if (!prev || !prev[leadId]) return prev;
      const next = { ...prev };
      delete next[leadId];
      return next;
    });
  }, []);

  // selectedLead now comes from TanStack Query (useLeadDetail) instead of local leads array
  const { data: selectedLead = null } = useLeadDetail(selectedLeadId);

  // MF-07 AC-8: after a 201 the new card opens exactly like a click on that lead on
  // the page the create was started from — the board's own opener (handleSelectLead,
  // `/?lead=`) and the bloggers page's (BloggersView.onOpenLead, `/bloggers?lead=`),
  // no jump to another page. Navigate FIRST: setting selectedLeadId/isDetailsOpen
  // without a `?lead=` in the URL lets the URL→state effect above close the pane on
  // the next commit — that is why the pane never opened after a create.
  const openCreatedLead = useCallback((leadId) => {
    if (!leadId) return;
    const basePath = location.pathname === '/bloggers' ? '/bloggers' : '/';
    navigate(`${basePath}?lead=${leadId}`);
    setSelectedLeadId(leadId);
    setIsDetailsOpen(true);
  }, [location.pathname, navigate]);

  // ── Lead editor ──
  const {
    isEditorOpen, editorSaving, editorError,
    openCreateLeadModal,
    handleEditorSubmit, closeEditor
  } = useLeadEditor({
    statuses, user,
    openCreatedLead, setIsDetailsOpen, setBusyLeadId,
    queryClient
  });

  // ── Create blogger form state (for unified CreateBloggerModal) ──
  const [createBloggerForm, setCreateBloggerForm] = useState({
    name: '', realName: '', email: '', phone: '', geo: '',
    instagram: '', tiktok: '', youtube: '',
    price: '', priceMonthly: '', currency: 'USD', comment: '',
    application: 'AR Drawing',
    tagLabel: '',
  });
  const handleCreateFormFieldChange = (field, value) => {
    setCreateBloggerForm(prev => ({ ...prev, [field]: value }));
  };
  // Reset form when create modal opens
  useEffect(() => {
    if (isEditorOpen) {
      setCreateBloggerForm({
        name: '', realName: '', email: '', phone: '', geo: '',
        instagram: '', tiktok: '', youtube: '',
        price: '', priceMonthly: '', currency: 'USD', comment: '',
        application: 'AR Drawing',
        tagLabel: '',
      });
    }
  }, [isEditorOpen]);

  // ── DnD ──
  const {
    draggedLeadId, dragTargetStatus,
    handleCardDragStart, handleCardDragEnd,
    handleColumnDragOver, handleColumnDrop, handleColumnDragLeave,
    handleStatusChange
  } = useDragDrop({ changeStatusMutation, addToast, selectedLead });





  // Response interceptor + fetchAuthContext + bootstrap → moved to useAuth hook

  // Admin/team-lead resource loading + stats refresh → useAdminData hook

  useEffect(() => {
    if (activeView !== 'kanban') {
      setIsDetailsOpen(false);
    }
  }, [activeView]);



  // Messages now loaded inside LeadDetailsModal via useMessages hook

  // ── SSE: TanStack Query cache invalidation (replaces old manual SSE effect) ──
  useRealtimeInvalidation(token, {
    onLeadDeleted: (leadId) => {
      if (selectedLeadIdRef.current === leadId) {
        setSelectedLeadId(null);
        setIsDetailsOpen(false);
      }
    },
    onActivity: ({ leadId, activity }) => {
      setIncomingActivity({ leadId, activity });
    },
    onAutopilotTimer: (payload) => {
      const leadId = payload?.leadId;
      const seconds = Number(payload?.seconds);
      if (!leadId || !Number.isFinite(seconds) || seconds <= 0) return;
      const startedAt = payload?.startedAt ? Date.parse(payload.startedAt) : Date.now();
      const safeStartedAt = Number.isFinite(startedAt) ? startedAt : Date.now();
      const deadline = safeStartedAt + seconds * 1000;
      setActiveTimers(prev => ({
        ...prev,
        [leadId]: {
          leadId,
          nodeId: typeof payload?.nodeId === 'string' ? payload.nodeId : null,
          seconds,
          startedAt: safeStartedAt,
          deadline,
          scope: typeof payload?.scope === 'string' ? payload.scope : 'followup'
        }
      }));
    },
    onTimerExpire: handleTimerExpire
  });

  const statusMap = useMemo(
    () => new Map(statuses.map(status => [status.id, status])),
    [statuses]
  );

  const parentStatuses = useMemo(
    () => statuses.filter(s => s.parentId === null && s.id !== 'parent_ad'),
    [statuses]
  );

  // Board stats loaded via TanStack Query (useBoardStats hook)
  const stats = boardStats || { total: 0, active: 0, completed: 0, inbox: 0 };

  // handleLogin, handleLogout, handleImpersonate, handleExitImpersonation → useAuth hook

  const handleSelectLead = (leadId) => {
    navigate(`/?lead=${leadId}`);
    setSelectedLeadId(leadId);
    closeEditor();
    setIsDetailsOpen(true);
    // Lead data is fetched automatically by useLeadDetail(selectedLeadId)
  };



  const handleDeleteLead = async (leadId) => {
    if (!leadId) return;
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Удалить карточку лида вместе с историей?');
      if (!confirmed) return;
    }

    setDeletingLeadId(leadId);
    setBusyLeadId(leadId);
    deleteLeadMutation.mutate(leadId, {
      onSuccess: () => {
        if (selectedLeadId === leadId) {
          setSelectedLeadId(null);
        }
        setIsDetailsOpen(false);
      },
      onError: (err) => {
        addToast('Ошибка', `Не удалось удалить лид: ${err.message}`, 'error');
      },
      onSettled: () => {
        setDeletingLeadId(null);
        setBusyLeadId(null);
      }
    });
  };

  // DnD handlers → useDragDrop hook

  const handleUpdateLead = async (leadId, payload) => {
    if (!leadId || !payload || typeof payload !== 'object') return;
    setBusyLeadId(leadId);
    try {
      const lead = await updateLead(leadId, payload);
      queryClient.setQueryData(queryKeys.leads.detail(leadId), lead);
      queryClient.invalidateQueries({ queryKey: ['leads', 'column'] });
    } catch (err) {
      console.error('Failed to update lead field', err);
    } finally {
      setBusyLeadId(null);
    }
  };

  // Lead editor handlers → useLeadEditor hook

  // Messaging handlers moved into LeadDetailsModal

  const handleAssignOwner = async (leadId, ownerId) => {
    if (!leadId) return;
    setBusyLeadId(leadId);
    try {
      const lead = await updateLead(leadId, { ownerId: ownerId || null });
      queryClient.setQueryData(queryKeys.leads.detail(lead.id), lead);
      queryClient.invalidateQueries({ queryKey: ['leads', 'column'] });
    } catch (err) {
      console.error('Failed to assign manager', err);
    } finally {
      setBusyLeadId(null);
    }
  };




  // Team/account/manager CRUD, test leads, stats, AI config → useAdminData hook










  if (location.pathname === '/reset-password') {
    return <ResetPasswordView />;
  }

  if (!token) {
    return <LoginView onLogin={handleLogin} loading={authLoading} />;
  }

  if (!user) {
    return (
      <div className='loading-screen'>
        Загрузка данных…
      </div>
    );
  }

  // ── SCOPE-LEAD-ADD P3 (MF-16) §3.4: the `/add` shell bypass ──
  // Placed AFTER the two auth guards above (`!token` → <LoginView>, `!user` → the loading
  // screen) so a logged-out phone gets the login card ON `/add` and stays there once it is
  // in (`useAuth.js:90-92` navigates only for ACCOUNTANT), and immediately BEFORE the
  // `.app-shell` return below, whose `100vh`/`overflow:hidden` and fixed sidebar are what
  // makes the app unusable on a phone. Legal as an early return: no React hook is declared
  // past `:440` of this file, so hook order cannot change on any render.
  //
  // `<AccountantRouteGuard>` is RE-WRAPPED here, not reused — the live instance sits inside
  // `<main className='workspace'>` further down and is unreachable from this return. The
  // guard is a pure function of pathname + role, so two instances on disjoint paths cannot
  // disagree, and bypassing the router does not bypass the whitelist (AC-2).
  //
  // Ruling 3 — nothing navigates from this page: `openCreatedLead` is NOT passed and the
  // create takes the strip's own exit `{ openCreated: false }`. Nothing is added to
  // `supportedPaths` either, so a `?lead=` on `/add` is inert twice over. Only these two
  // exact strings bypass the shell; `/add/x` falls through to the catch-all (Risk 2).
  if (location.pathname === '/add' || location.pathname === '/add/') {
    return (
      <AccountantRouteGuard>
        <MobileAddPage
          onSubmit={(payload) => handleEditorSubmit(payload, { openCreated: false })}
          disabled={statuses.length === 0}
        />
      </AccountantRouteGuard>
    );
  }

  return (
    <div className='app-shell'>
      {impersonatedBy && (
        <div className="impersonation-banner">
          <span>Вы просматриваете как: <strong>{user?.name || user?.email}</strong></span>
          <button onClick={handleExitImpersonation}>Выйти в админа</button>
        </div>
      )}

      <Sidebar
        user={user}
        isAdmin={isAdmin}
        adminUsers={adminUsers}
        onLogout={handleLogout}
        onImpersonate={handleImpersonate}
        onOpenLead={(leadId, initialTab) => {
          // BCS P6 — the tab travels in the URL. The §6.7 change-1 effect assigns
          // `selectedLeadInitialTab` unconditionally on every supported-path run, so an
          // imperative setter here is overwritten by the tab-less URL one render later.
          navigate(`/?lead=${leadId}${initialTab ? `&tab=${initialTab}` : ''}`);
          setSelectedLeadId(leadId);
          setSelectedLeadInitialTab(initialTab || 'info');
          setIsDetailsOpen(true);
        }}
        impersonatedBy={impersonatedBy}
        onExitImpersonation={handleExitImpersonation}
      />

      <main className='workspace'>
        {/* Phase 66 D-07: ACCOUNTANT-only route whitelist. Wraps <Routes> so the
            guard sees the active pathname and can redirect non-allowed paths to
            /invoices. ADMIN/TEAM_LEAD/MANAGER pass through unchanged. */}
        <AccountantRouteGuard>
          <Routes>
          <Route path="/" element={
            <section className='workspace__primary'>
              <div className='board-toolbar'>
                <input
                  className='form-input board-toolbar__search'
                  type='text'
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder='Поиск'
                />
                {(isAdmin || isTeamLead) && (
                  <FilterDropdown
                    value={managerFilter}
                    onChange={setManagerFilter}
                    placeholder='Все менеджеры'
                    options={[
                      { value: 'all', label: 'Все менеджеры' },
                      { value: 'unassigned', label: 'Без менеджера', dot: true, dotStyle: { background: '#f59e0b' } },
                      // FIX-1: include the viewing TEAM_LEAD themselves (the members
                      // endpoint lists only MANAGERs) so a team-lead can filter to own leads.
                      ...managerFilterUsers({ user, isTeamLead, adminUsers }).map(m => ({
                        value: m.id,
                        label: (m.name || m.email) + (m.id === user?.id ? ' (вы)' : ''),
                        dot: true,
                        dotStyle: { background: '#60a5fa' },
                      })),
                    ]}
                  />
                )}
                <FilterDropdown
                  value={lastContactFilter}
                  onChange={setLastContactFilter}
                  placeholder='Последний контакт'
                  options={[
                    { value: 'all', label: 'Последний контакт' },
                    { value: '7', label: '7 дней' },
                    { value: '14', label: '14 дней' },
                    { value: '30', label: 'Месяц' },
                  ]}
                />
                <button
                  type='button'
                  className={`board-action-btn${showUnreadOnly ? ' board-action-btn--active' : ''}`}
                  onClick={() => setShowUnreadOnly(v => !v)}
                >
                  Непрочитанное
                </button>
                <button
                  className='board-action-btn'
                  type='button'
                  onClick={() => setCsvImportOpen(true)}
                >
                  Импорт CSV
                </button>
                <button
                  className='board-action-btn board-action-btn--active'
                  type='button'
                  onClick={() => openCreateLeadModal()}
                  disabled={statuses.length === 0}
                >
                    Добавить блогера
                </button>
                {/* SCOPE-LEAD-ADD P1 §3.1: the strip lives in the toolbar itself,
                    which already wraps (`styles.css:641`), so no media query and no
                    shell change. `openCreated: false` is ruling 4 — the strip stays
                    in place instead of opening the new card. */}
                <QuickAddStrip
                  onSubmit={(payload) => handleEditorSubmit(payload, { openCreated: false })}
                  onOpenLead={openCreatedLead}
                  disabled={statuses.length === 0}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                  {stats.total > 0 ? `${stats.total.toLocaleString('ru-RU')} блогеров` : ''}
                </span>
              </div>

              <div className='kanban'>
                {parentStatuses.map((status, idx) => (
                  <KanbanColumn
                    key={`${status.id}-${idx}`}
                    status={status}
                    isAdmin={isAdmin}
                    selectedLeadId={selectedLeadId}
                    draggedLeadId={draggedLeadId}
                    dragTargetStatus={dragTargetStatus}
                    busyLeadId={busyLeadId}
                    searchQuery={searchQuery}
                    managerFilter={managerFilter}
                    onSelectLead={handleSelectLead}
                    onCardDragStart={handleCardDragStart}
                    onCardDragEnd={handleCardDragEnd}
                    onDeleteLead={handleDeleteLead}
                    onColumnDragOver={handleColumnDragOver}
                    onColumnDrop={handleColumnDrop}
                    onColumnDragLeave={handleColumnDragLeave}
                    onOpenCreateModal={openCreateLeadModal}
                    unreadLeadIds={unreadLeadIds}
                    unreadCounts={unreadCounts}
                    showUnreadOnly={showUnreadOnly}
                    statusLookup={statusMap}
                    userId={user?.id}
                    allStatuses={statuses}
                    lastContactDays={lastContactFilter}
                  />
                ))}
              </div>
            </section>
          } />

          <Route path="/bloggers" element={
            <RouteErrorBoundary section="Блогеры">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка блогеров..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  {/* §3.1(c): the page header, not the «Мои» toolbar — the strip must
                      be visible on every tab (ruling 3), and BloggersMineTab is pinned
                      to carry no create surface (`handle-seam.test.js:419-428`). */}
                  <QuickAddStrip
                    onSubmit={(payload) => handleEditorSubmit(payload, { openCreated: false })}
                    onOpenLead={openCreatedLead}
                    disabled={statuses.length === 0}
                  />
                  <BloggersView statuses={statuses} scouts={adminUsers} onOpenLead={(leadId) => { navigate(`/bloggers?lead=${leadId}`); setSelectedLeadId(leadId); setIsDetailsOpen(true); }} />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          <Route path="/finder" element={
            <RouteErrorBoundary section="Поиск блогеров">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка поиска..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <FinderView />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          {/* Phase 93 Plan 08 D-93-07: thin first-cut payout view — invoice list + «+ Бонус» */}
          <Route path="/ambassadors/:id" element={
            <RouteErrorBoundary section="Payout">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <AmbassadorPayoutView />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          <Route path="/documents" element={
            <RouteErrorBoundary section="Амбассадоры">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка амбассадоров..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <AmbassadorsView />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          <Route path="/invoices" element={
            <RouteErrorBoundary section="Инвойсы">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка инвойсов..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <InvoicesView />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          {/* INBOX-OFF (owner 13.10): the messaging switch (user.messagingEnabled from /auth/me) hides «Входящие» for every role. */}
          <Route path="/inbox" element={user?.messagingEnabled !== true ? <Navigate to="/" replace /> :
            <RouteErrorBoundary section="Входящие">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка входящих..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <InboxPage
                    onOpenLead={(leadId, initialTab) => {
                      // BCS P6 — the tab travels in the URL (see the Sidebar handler above).
                      navigate(`/inbox?lead=${leadId}${initialTab ? `&tab=${initialTab}` : ''}`);
                      setSelectedLeadId(leadId);
                      setSelectedLeadInitialTab(initialTab || 'info');
                      setIsDetailsOpen(true);
                    }}
                  />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          {/* M2-HIDE-OLD (owner 2026-09-24): the old «Мониторинг» is hidden — bookmarks land on «Мониторинг 2». */}
          <Route path="/monitoring/*" element={<Navigate to="/monitoring2" replace />} />

          <Route path="/monitoring2/*" element={
            <RouteErrorBoundary section="Мониторинг">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка мониторинга..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <MonitoringComponent
                    onOpenLead={(leadId, initialTab) => {
                      const keep = monitoring2ListParams(location.search);
                      keep.set('lead', leadId);
                      keep.set('tab', initialTab || 'monitoring');
                      navigate(`${monitoringReturnPath(location.pathname, '')}?${keep.toString()}`);
                      setSelectedLeadId(leadId);
                      setSelectedLeadInitialTab(initialTab || 'monitoring');
                      setIsDetailsOpen(true);
                    }}
                  />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          {/* AMBSTAT-HIDE-MANAGERS (owner 13.10): exact MANAGER loses «Амбстат», BMAP form. */}
          <Route path="/ambstat/*" element={user?.role === 'MANAGER' ? <Navigate to="/" replace /> :
            <RouteErrorBoundary section="Амбстат">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка амбстата..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <AmbstatView />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          <Route path="/outreach/*" element={user?.role === 'MANAGER' ? <Navigate to="/" replace /> :
            <RouteErrorBoundary section="Аутрич">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка аутрича..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <OutreachProvider onOpenLead={openLeadFromOutreach}
                      teams={adminTeams}
                      isAdmin={isAdmin}
                      isTeamLead={isTeamLead}>
                    <OutreachLayout />
                  </OutreachProvider>
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

            <Route path="/stats" element={user?.role === 'MANAGER' ? <Navigate to="/" replace /> :
              <RouteErrorBoundary section="Статистика">
                <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка статистики..." /></section>}>
                  <section className='workspace__primary workspace__primary--alt'>
                    <MyStatisticsPage userRole={user?.role} userId={user?.id} />
                  </section>
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="/team" element={user?.role === 'MANAGER' ? <Navigate to="/" replace /> :
              <RouteErrorBoundary section="Команда">
                <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка команды..." /></section>}>
                  <section className='workspace__primary workspace__primary--alt'>
                    <TeamPage userRole={user?.role} userId={user?.id} />
                  </section>
                </Suspense>
              </RouteErrorBoundary>
            } />

          <Route path="/settings" element={
            <RouteErrorBoundary section="Настройки">
              <Suspense fallback={<section className='workspace__primary workspace__primary--alt'><LoadingSpinner text="Загрузка настроек..." /></section>}>
                <section className='workspace__primary workspace__primary--alt'>
                  <SettingsPanel
                    users={adminUsers}
                    teams={adminTeams}
                    isAdmin={isAdmin}
                    isTeamLead={isTeamLead}
                    currentUser={user}
                    loading={settingsLoading}
                    onToggleManagerActive={handleToggleManagerActive}
                    onOpenManagerModal={openManagerModal}
                    onEditManager={handleEditManager}
                    onCreateTeam={handleCreateTeam}
                    onEditTeam={handleEditTeam}
                    onDeleteTeam={handleDeleteTeam}
                  />
                </section>
              </Suspense>
            </RouteErrorBoundary>
          } />

          {/* Phase 97.1-04 — admin OR team_lead can access (pivot 2026-05-18).
              POST-W1 T3 (2026-08-27, owner): the page moved into «Настройки» as a tab, so the
              route is kept only as a bookmark alias and redirects there. The role gate is
              unchanged — it now lives on the tab itself (SettingsPanel.jsx). */}
          <Route path="/admin/integrations/replyio/mapping" element={
            (isAdmin || isTeamLead)
              ? <Navigate to="/settings?tab=replyio" replace />
              : <Navigate to="/" replace />
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AccountantRouteGuard>
      </main>

      {isDetailsOpen && selectedLead && (
        <LeadDetailsModal
          open
          lead={selectedLead}
          statuses={statuses}
          statusLookup={statusMap}
          onClose={() => {
            setIsDetailsOpen(false);
            setSelectedLeadInitialTab('info');
            if (location.pathname === '/monitoring2' || location.pathname.startsWith('/monitoring2/')) {
              navigate(monitoringReturnPath(location.pathname, location.search), { replace: true });
              return;
            }
            // Keep ?mode=amb on /monitoring — closing the modal must not flip the page back to test mode.
            const monMode = new URLSearchParams(location.search).get('mode');
            navigate(monMode === 'amb' ? `${location.pathname}?mode=amb` : location.pathname, { replace: true });
          }}
          initialTab={selectedLeadInitialTab}
          onEditSubmit={async (payload) => {
            if (!selectedLead?.id) return;
            try {
              const metadata = buildLeadMetadata(selectedLead?.metadata || null, {
                negotiationMin: payload.negotiationMin ? Number(payload.negotiationMin) : null,
                negotiationMax: payload.negotiationMax ? Number(payload.negotiationMax) : null,
                payment: payload.payment || null,
              });
              const body = { ...payload, metadata };
              delete body.negotiationMin;
              delete body.negotiationMax;
              delete body.payment;
              if (body.priceMonthly !== undefined) {
                body.priceMonthly = body.priceMonthly ? parseFloat(body.priceMonthly) : null;
              }
              const updated = await updateLead(selectedLead.id, body);
              queryClient.setQueryData(queryKeys.leads.detail(selectedLead.id), updated);
              queryClient.invalidateQueries({ queryKey: ['leads', 'column'] });
              // Phase 86 Wave 6 (P-21 4-spot coverage): inline EditTab save
              // must invalidate the per-lead accounts cache so InfoTab +
              // MonitoringTab pick up the canonical TrackedAccount rows the
              // PATCH handler just wrote (per 86-04 accounts[] consumption).
              queryClient.invalidateQueries({ queryKey: queryKeys.accounts.detail(selectedLead.id) });
            } catch (err) {
              // PACK-UI §2.7 — a removal the shared `deleteExtraAccount` guard refused
              // (409 account_in_use / 503 metrics_unavailable) must reach the manager:
              // re-thrown so the card keeps edit mode open and renders the reason
              // (LeadDetailsInfoTab.handleInlineSave). Nothing was mutated server-side;
              // the accounts cache is still invalidated (§4.9) — a harmless refetch that
              // keeps the tab's server list fresh, while the edit form deliberately keeps
              // the manager's draft (`extrasDirty`, LeadDetailsInfoTab.jsx:450-452) so the
              // reason line refers to the list they were saving.
              // Every other failure keeps the previous console-only behaviour.
              const code = err?.data?.error;
              if (code === 'account_in_use' || code === 'account_handle_locked' || code === 'metrics_unavailable' || code === 'invalid_handle') {
                queryClient.invalidateQueries({ queryKey: queryKeys.accounts.detail(selectedLead.id) });
                throw err;
              }
              console.error('Failed to update lead inline', err);
            }
          }}
          onDelete={() => handleDeleteLead(selectedLead.id)}
          currentUser={user}
          managers={adminUsers}
          onAssignOwner={isAdmin ? handleAssignOwner : null} // S13: reassign ADMIN-only
          onStatusChange={handleStatusChange}
          ownerUpdating={busyLeadId === selectedLead.id}
          deleting={deletingLeadId === selectedLead.id}
          timer={activeTimers[selectedLead.id] || null}
          onTimerExpire={handleTimerExpire}
          incomingActivity={incomingActivity}
        />
      )}

      <ManagerCreateModal
        open={isManagerModalOpen}
        loading={settingsLoading}
        form={managerForm}
                  teams={adminTeams}
                  isAdmin={isAdmin}
                  isTeamLead={isTeamLead}
        isEditing={Boolean(editingManager)}
        onClose={closeManagerModal}
        onFieldChange={handleManagerFormFieldChange}
        onSubmit={handleCreateManager}
      />

      {/* Create mode → CreateBloggerModal */}
      <CreateBloggerModal
        open={isEditorOpen}
        loading={editorSaving}
        form={createBloggerForm}
        isEditing={false}
        onClose={closeEditor}
        onFieldChange={handleCreateFormFieldChange}
        onSubmit={(payload) => handleEditorSubmit(payload)}
        submitError={editorError?.reason || null}
        duplicateWarning={editorError?.duplicate || null}
      />

      <CsvImportModal
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['leads'] })}
      />
    </div>
  );
}

export default App;
